import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Languages, Check } from 'lucide-react';
import { cn } from "@/lib/utils";

export default function LgDropdownMenu({ scrolled }) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "en", name: "English", short: "EN" },
    { code: "ar", name: "العربية", short: "ع" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const changeLanguage = (languageCode) => {
    if (i18n.language === languageCode) {
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    i18n.changeLanguage(languageCode).then(() => {
      // Persist language choice
      if (typeof window !== "undefined") {
        window.localStorage.setItem('i18nextLng', languageCode);
      }
      // Update document direction based on language
      document.documentElement.dir = languageCode === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = languageCode;
      
      setTimeout(() => {
        setIsLoading(false);
        setIsOpen(false);
        // Optionally: window.location.reload(); // if a full reload is needed for some rtl/ltr styles to apply correctly
      }, 500); // Reduced timeout for quicker feedback
    });
  };

  const currentSelectedLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <div className="relative flex z-10" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleDropdown}
        className={cn(
          "transition-colors duration-300 rounded-full w-9 h-9",
          scrolled
            ? "text-foreground hover:bg-muted"
            : "text-primary-foreground hover:bg-white/10 md:text-foreground md:hover:bg-muted"
        )}
        aria-label={t('common.changeLanguage', 'Change language')}
      >
        <Languages className="h-5 w-5" />
      </Button>

      {isOpen && (
        <div 
          className={cn(
            "absolute top-full mt-2 w-48 rounded-md shadow-lg z-50 overflow-hidden",
            "bg-popover border border-border",
            i18n.language === 'ar' ? "left-0" : "right-0"
          )}
        >
          <ul className="py-1">
            {languages.map((lang) => (
              <li key={lang.code}>
                <button
                  onClick={() => changeLanguage(lang.code)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-sm text-popover-foreground",
                    "hover:bg-muted focus:bg-muted focus:outline-none transition-colors duration-150",
                    i18n.language === lang.code && "font-semibold bg-muted/50"
                  )}
                  disabled={isLoading && i18n.language !== lang.code}
                >
                  <span>{lang.name} ({lang.short})</span>
                  {i18n.language === lang.code && <Check className="h-4 w-4 text-primary" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
